import { UserNoteFilterInput } from 'dy-frontend-http-repository/lib/modules/UserNote/inputs';
import { useStore } from 'effector-react';
import React, { useCallback, useEffect } from 'react';

import { usePageTitle } from '@app/hooks';
import { Button, Icon, Spinner } from '@blueprintjs/core';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import NonIdealState from '@components/NonIdealState';
import { $permissions } from '@containers/store/states';
import { $client } from '@pages/Client/store/states';

import ClientNoteEditor from './components/ClientNoteEditor';
import ClientNoteListItem from './components/ClientNoteListItem';
import { clientNotesApi } from './store/apis';
import { fetchClientNotes } from './store/effects';
import { resetClientNotes, resetEditingClientNote } from './store/events';
import { $clientNotes, $editingClientNote } from './store/states';

import { amountOfNotesOnPage } from './consts';

const ClientNotes: React.FC = () => {
    const permissions = useStore($permissions);

    const { changeTitle } = usePageTitle('Client Notes');

    const client = useStore($client);
    const clientNotes = useStore($clientNotes);
    const editingClientNote = useStore($editingClientNote);
    const isFetchingClientNotes = useStore(fetchClientNotes.pending);

    const handleFetchNext = useCallback(async () => {
        if (!client) return new Promise(() => '');

        // Common filters
        const filter: UserNoteFilterInput = { user_id: client.id, is_archived: '0' };

        let cursor: undefined | ID;

        if (clientNotes && clientNotes.cursor && clientNotes.cursor.next_cursor_start !== null) {
            cursor = clientNotes.cursor.next_cursor_start;
        }

        if (!permissions.isEnabled.userNote) {
            return;
        }

        try {
            // Fetch client notes
            const notes = await fetchClientNotes({
                pagination: { _cursor: { direction: 'desc', limit: amountOfNotesOnPage, start: cursor } },
                filter,
            });

            // Add client notes at the end
            clientNotesApi.addAtTailAfterSuccessfulFetch({ clientNotes: notes });
        } catch (e) {
            // TODO: handle error
            console.error(e);
        }
    }, [client, clientNotes]);

    useEffect(() => {
        handleFetchNext();
    }, []);

    useEffect(() => {
        // This happens in the situation whenever all client notes were removed, but there are more client notes to show, which should be fetched
        if (clientNotes && clientNotes.items.length === 0 && clientNotes.cursor && clientNotes.cursor.has_more) {
            handleFetchNext();
        }
    }, [clientNotes]);

    useEffect(() => {
        if (!client) {
            return;
        }

        changeTitle(`Client Notes - ${client.first_name} ${client.last_name}`);
    }, [client?.first_name, client?.last_name]);

    useEffect(() => {
        return () => {
            resetClientNotes();
            resetEditingClientNote();
        };
    }, []);

    if (!permissions.isEnabled.userNote) {
        return null;
    }

    if (!client) {
        return null;
    }

    const renderClientNotesList = () => {
        if (!clientNotes) {
            return (
                <Flex align="center" justify="center">
                    <Spinner />
                </Flex>
            );
        }

        // Client notes were fetched, but where are no notes existed for this client
        if (clientNotes && clientNotes.items.length === 0 && clientNotes.cursor && !clientNotes.cursor.has_more) {
            return (
                <NonIdealState
                    className="mt-4"
                    icon={<Icon className="mb-2" icon="search" size={70} />}
                    title={
                        <Heading type="h4" className="mb-1">
                            No client notes were found
                        </Heading>
                    }
                />
            );
        }

        return (
            <div>
                {clientNotes?.items.map((clientNote) => (
                    <ClientNoteListItem
                        className="mt-2"
                        key={clientNote.id}
                        editingNote={editingClientNote}
                        note={clientNote}
                    />
                ))}

                {clientNotes && clientNotes.cursor && clientNotes.cursor.has_more && (
                    <Flex className="mt-2" justify="center" align="center">
                        <Button
                            loading={isFetchingClientNotes}
                            outlined
                            icon="refresh"
                            rightIcon="refresh"
                            onClick={handleFetchNext}
                        >
                            Show more
                        </Button>
                    </Flex>
                )}
            </div>
        );
    };

    return (
        <div>
            <ClientNoteEditor className="mb-4" />
            {renderClientNotesList()}
        </div>
    );
};

export default ClientNotes;
